'use client';

import React, { SyntheticEvent, useCallback, useState } from 'react';
import Icon from '@/components/shared/Icons';
import { getAvailabilityOutput } from '@/utils/ibk/getIbkAvailabilityOutput';
import { formatMessage } from '@/utils/translationHelper';
import type { Locale } from '@/i18n';
import dynamic from 'next/dynamic';
import useIbkAvailability from '@/hooks/useIbkAvailability';
import { getSimpleClickFact } from '@/analytics/helpers/cventAnalytics/getSimpleClickFact';
import {
  INSTANT_BOOK_WIDGET_TRIGGER_CLICK_TYPE,
  INSTANT_BOOK_FROM_WIDGET_CLICK_TYPE
} from '@/utils/analyticsClientHelper';
import useMap from '@/hooks/useMap';
import { SimpleClickFact } from '@/analytics/models/cventAnalytics/SimpleClickFact';
import useAnalytics from '@/hooks/useAnalytics';
import { getFilterSelections } from '@/utils/ibk/getFilterSelections';
import useLogger from '@/hooks/useLogger';

const CheckAvailability = dynamic(() => import('@/components/card/ibk/CheckAvailability'), { ssr: false });

type MeetingRooms = {
  averageCostPerDayBeforeTax:
    | {
        amount: number;
      }
    | number;
  totalCostBeforeTax:
    | {
        amount: number;
      }
    | number;
  currency: string;
};

type SleepingRooms = {
  lowestAverageRateWithoutTax:
    | {
        amount: number;
      }
    | number;
  lowestAverageRateWithTax:
    | {
        amount: number;
      }
    | number;
  currency: string;
};

export type InstantBookableVenue = {
  meetingSpaceIntegration: boolean;
  sleepingSpaceIntegration: boolean;
  meetingRooms?: MeetingRooms[];
  sleepingRooms?: SleepingRooms[];
};

export type IbkStrings = {
  _csn_search_srp_ibk_banner_instant_book_text: string;
  csn_ibk_booking_notAvailable: string;
  csn_ibk_only_meetingRooms: string;
  csn_ibk_only_guestRooms: string;
  csn_ibk_guestRooms_not_bookable: string;
  csn_ibk_booking_meetingRoomRate: string;
  csn_ibk_booking_guestRoomRate: string;
  csn_ibk_meetingRooms_not_bookable: string;
  csn_ibk_guestRooms_not_available: string;
  csn_ibk_meetingRooms_not_available: string;
  _venue_profile_section_ibkWidget_rfpWarning_header: string;
  _venue_profile_section_ibkWidget_rfpWarning_secondaryButton: string;
  _venue_profile_section_ibkWidget_rfpWarning_primaryButton: string;
  _venue_profile_section_ibkWidget_rfpWarning_body: string;
  csn_search_availability_modal_venueAvailable: string;
  csn_search_availability_modal_meetingRoomsOnly_Available: string;
  csn_search_availability_modal_guestRoomsOnly_Available: string;
  csn_search_availability_modal_inventoryNotAvailable: string;
  csn_search_availability_modal_numOfPeople: string;
  csn_search_availability_modal_startDate: string;
  csn_search_availability_modal_endDate: string;
  csn_search_availability_modal_roomsPerNight: string;
  csn_search_availability_modal_checkIn: string;
  csn_search_availability_modal_checkOut: string;
  csn_search_availability_modal_header: string;
  csn_search_availability_modal_selectRoomsButton: string;
  csn_search_availability_modal_checkAvailabilityButton: string;
  csn_search_availability_modal_meetingRoomTitle: string;
  csn_search_availability_modal_guestRoomTitle: string;
  _close: string;
  csn_search_guest_rooms_date_range_label: string;
  csn_search_date_range_dialog: string;
  csn_search_date_range_previous_month: string;
  csn_search_date_range_next_month: string;
  csn_search_guest_rooms_date_range_start_dialog: string;
  csn_search_guest_rooms_date_range_end_dialog: string;
  csn_ibk_booking_banner_check_availability_button_label: string;
  csn_ibk_booking_banner_book_now_button_label: string;
};

type IBKBannerProps = {
  venueId: string;
  venueIndex: number;
  instantBookableVenue: InstantBookableVenue;
  strings: IbkStrings;
  locale: Locale;
  cventGuestDomain: string;
  rfpName: string;
  searchResultsRank: number;
  searchTerm: string;
  activeFilters: string;
  diamondLevel: string | undefined;
  isIbkAvailabilityCalendarEnabled: boolean;
};

export type BannerContent = {
  primaryRoomAvailability: string;
  secondaryRoomAvailability?: string;
  isBookNowAvailable?: boolean;
};

export default function IbkBanner({
  venueId,
  venueIndex,
  instantBookableVenue,
  strings,
  locale,
  cventGuestDomain,
  rfpName,
  searchResultsRank,
  searchTerm,
  activeFilters,
  diamondLevel,
  isIbkAvailabilityCalendarEnabled
}: IBKBannerProps): JSX.Element {
  const { IBK_DATA } = useIbkAvailability();
  const logger = useLogger('IbkBanner');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { preFilledFilters, configSkuJWT } = IBK_DATA;
  const {
    preFilledMeetingSpaceAttendees,
    preFilledStartDate,
    preFilledEndDate,
    preFilledGuestRoomCount,
    preFilledGuestRoomStartDate,
    preFilledGuestRoomEndDate
  } = preFilledFilters;
  const eventDatesAvailable = !!(preFilledStartDate && preFilledEndDate);
  const guestRoomDatesAvailable = !!(preFilledGuestRoomStartDate && preFilledGuestRoomEndDate);
  const averageCostPerDayBeforeTax = instantBookableVenue.meetingRooms?.[0]?.averageCostPerDayBeforeTax;

  // after DW4 upgrade of csn-search-app-service, it will return averageCostPerDayBeforeTax as a number
  const meetingRoomCost =
    typeof averageCostPerDayBeforeTax === 'object' ? averageCostPerDayBeforeTax?.amount : averageCostPerDayBeforeTax;

  const { mapExpanded } = useMap();
  const analytics = useAnalytics();

  const lowestAverageRateWithoutTax = instantBookableVenue.sleepingRooms?.[0]?.lowestAverageRateWithoutTax;
  const lowestAverageRateWithTax = instantBookableVenue.sleepingRooms?.[0]?.lowestAverageRateWithTax;

  const withoutTaxAmount =
    typeof lowestAverageRateWithoutTax === 'object' ? lowestAverageRateWithoutTax?.amount : lowestAverageRateWithoutTax;
  const withTaxAmount =
    typeof lowestAverageRateWithTax === 'object' ? lowestAverageRateWithTax?.amount : lowestAverageRateWithTax;

  const guestRoomCost = withoutTaxAmount ?? withTaxAmount;

  const ibkRatesAvailable = !!(meetingRoomCost || guestRoomCost);
  const disabledModeOn = (eventDatesAvailable || guestRoomDatesAvailable) && !ibkRatesAvailable;
  const separator = ' • ';
  const triggerSimpleClickFact = useCallback(
    async (e, factType: string, stopPropagation = true) => {
      if (stopPropagation) {
        e.stopPropagation();
      }
      const simpleClickFact = getSimpleClickFact(
        venueId,
        factType,
        searchResultsRank,
        venueId,
        mapExpanded,
        searchTerm,
        diamondLevel,
        activeFilters
      );
      analytics.reportFact<SimpleClickFact>(simpleClickFact);
    },
    [venueId, searchResultsRank, mapExpanded, searchTerm, diamondLevel, activeFilters, analytics]
  );

  const bannerHandler = useCallback(
    async (e: SyntheticEvent, isBookNowAvailable?: boolean) => {
      e.preventDefault();
      e.stopPropagation();
      if (isBookNowAvailable) {
        triggerSimpleClickFact(e, INSTANT_BOOK_FROM_WIDGET_CLICK_TYPE);
        const filterSelections = getFilterSelections(
          !!(preFilledStartDate && preFilledEndDate),
          !!(preFilledGuestRoomStartDate && preFilledGuestRoomEndDate),
          meetingRoomCost,
          guestRoomCost,
          preFilledStartDate,
          preFilledEndDate,
          preFilledGuestRoomStartDate,
          preFilledGuestRoomEndDate,
          preFilledMeetingSpaceAttendees,
          preFilledGuestRoomCount
        );
        const ibkAppWindow = window.open();
        try {
          const response = await fetch('/venues/api/filter', {
            method: 'POST',
            body: JSON.stringify(filterSelections)
          });

          if (response.ok) {
            const res = await response.json();
            const ibkShopAndBookUrl = `${cventGuestDomain}/instantbook/venue/${venueId}?selections=${
              res.encoded ?? ''
            }${configSkuJWT !== '' ? `&configSkuJWT=${configSkuJWT}` : ''}`;
            if (ibkAppWindow) {
              ibkAppWindow.location.href = ibkShopAndBookUrl;
            }
          }
        } catch (error) {
          logger.error('Failed to redirect to IBK from venue card');
        }
      } else {
        triggerSimpleClickFact(e, INSTANT_BOOK_WIDGET_TRIGGER_CLICK_TYPE);
        setIsModalOpen(!isModalOpen);
      }
    },
    [
      configSkuJWT,
      cventGuestDomain,
      guestRoomCost,
      isModalOpen,
      logger,
      meetingRoomCost,
      preFilledEndDate,
      preFilledGuestRoomCount,
      preFilledGuestRoomEndDate,
      preFilledGuestRoomStartDate,
      preFilledMeetingSpaceAttendees,
      preFilledStartDate,
      triggerSimpleClickFact,
      venueId
    ]
  );
  let currencyCode: string | undefined;
  if (meetingRoomCost || guestRoomCost) {
    if (instantBookableVenue.meetingRooms) {
      currencyCode = instantBookableVenue.meetingRooms[0]?.currency;
    } else {
      currencyCode = instantBookableVenue.sleepingRooms?.[0]?.currency;
    }
  }

  const instantBookableVenueProps = {
    isMeetingRoomIntegrated: instantBookableVenue.meetingSpaceIntegration,
    isSleepingRoomIntegrated: instantBookableVenue.sleepingSpaceIntegration,
    meetingRoomCost,
    guestRoomCost
  };

  const availabilityProps = {
    instantBookableVenue,
    meetingRoomCost,
    guestRoomCost,
    currencyCode,
    eventDatesAvailable,
    guestRoomDatesAvailable,
    preFilledMeetingSpaceAttendees,
    preFilledGuestRoomCount,
    strings,
    locale
  };
  const bannerContent: BannerContent = disabledModeOn
    ? { primaryRoomAvailability: formatMessage(strings.csn_ibk_booking_notAvailable, locale) }
    : getAvailabilityOutput(availabilityProps);

  const { primaryRoomAvailability, secondaryRoomAvailability, isBookNowAvailable } = bannerContent;

  return (
    <button
      type="button"
      data-cvent-id={`ibk-banner-wrapper-card-index-${venueIndex}`}
      className={`rounded-b-xl hover:rounded-b-xl ${
        disabledModeOn ? 'bg-neutral-20' : 'bg-tertiary-20'
      } px-4 py-2 lg:flex lg:justify-between`}
      onClick={e => bannerHandler(e, isBookNowAvailable)}
      tabIndex={0}
    >
      <div className="flex">
        <Icon id="bolt" className={`h-5 w-5 ${disabledModeOn ? 'fill-neutral-70' : 'fill-tertiary-70'}`} />
        <div
          className={`flex flex-col items-start gap-0.5 text-b-sm lg:flex-row lg:gap-1 ${
            disabledModeOn ? 'text-neutral-70' : 'text-tertiary-70'
          }`}
          data-cvent-id="ibk-banner-text"
        >
          <b>{strings._csn_search_srp_ibk_banner_instant_book_text}</b>
          <div
            className={` ${disabledModeOn ? 'text-neutral-70' : 'text-tertiary-70'}`}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: primaryRoomAvailability }}
          />
          {primaryRoomAvailability && secondaryRoomAvailability ? (
            <div className={`hidden lg:flex ${disabledModeOn ? 'text-neutral-70' : 'text-tertiary-70'}`}>
              {separator}
            </div>
          ) : null}
          {secondaryRoomAvailability ? (
            <div
              className={`${disabledModeOn ? 'text-neutral-70' : 'text-tertiary-70'}`}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: secondaryRoomAvailability }}
            />
          ) : null}
        </div>
      </div>
      <div className="flex justify-end">
        <Icon id="bolt" className={`h-5 w-5 ${disabledModeOn ? 'fill-neutral-70' : 'fill-interactive-70'}`} />
        <span className={`text-b-sm ${disabledModeOn ? 'text-neutral-70' : 'text-interactive-70'}`}>
          {isBookNowAvailable ? (
            <div data-cvent-id={`ibk-banner-button-wrapper-book-now-card-index-${venueIndex}`}>
              <b>{strings.csn_ibk_booking_banner_book_now_button_label}</b>
            </div>
          ) : (
            // don't want wrapper div to be accessible to screen reader
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
            <div>
              <CheckAvailability
                locale={locale}
                disabledModeOn={disabledModeOn}
                strings={strings}
                cventGuestDomain={cventGuestDomain}
                instantBookableVenueProps={instantBookableVenueProps}
                venueId={venueId}
                rfpName={rfpName}
                searchResultsRank={searchResultsRank}
                searchTerm={searchTerm}
                activeFilters={activeFilters}
                diamondLevel={diamondLevel}
                isIbkAvailabilityCalendarEnabled={isIbkAvailabilityCalendarEnabled}
                isModalOpen={isModalOpen}
                setIsModalOpen={setIsModalOpen}
                testID={`ibk-banner-button-wrapper-check-availability-card-index-${venueIndex}`}
              />
            </div>
          )}
        </span>
      </div>
    </button>
  );
}
